import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier, getPurchaseOrders, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder, getAccountsPayable, uploadPurchaseOrderInvoice, downloadPurchaseOrderInvoice } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ActionButton,
  ActionGroup,
  DataTable,
  EmptyState,
  MetricCard,
  PageHeader,
  StatsGrid,
  StatusBadge,
} from '../components/app';
import {
  PurchaseOrderCancelDialog,
  PurchaseOrderDialog,
  PurchaseOrderInvoiceDialog,
  SupplierDialog,
  createPurchaseOrderColumns,
} from '../components/suppliers';
import { Truck, Plus, ShoppingCart, ArrowClockwise, Trash, PencilSimple, CheckCircle, Paperclip } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function SuppliersPage() {
  const { isSpanish } = useLanguage();
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [accountsPayable, setAccountsPayable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  const [isPODialogOpen, setIsPODialogOpen] = useState(false);
  const [isCancelPODialogOpen, setIsCancelPODialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ name: '', contact_name: '', email: '', phone: '', address: '', tax_id: '', category: '', payment_terms: 30, notes: '' });
  const [poForm, setPOForm] = useState({ supplier_id: '', description: '', subtotal: 0, tax_amount: 0, total: 0, expected_date: '', notes: '' });
  const [poInvoiceFile, setPOInvoiceFile] = useState(null);
  const [bankTransferNumber, setBankTransferNumber] = useState('');
  const [activeTab, setActiveTab] = useState('suppliers');
  const invoiceInputRef = useRef(null);
  const invoiceUploadInputRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [supRes, poRes, apRes] = await Promise.all([
        getSuppliers({}),
        getPurchaseOrders({}),
        getAccountsPayable({})
      ]);
      setSuppliers(supRes.data);
      setPurchaseOrders(poRes.data);
      setAccountsPayable(apRes.data);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await updateSupplier(editingSupplier._id, supplierForm);
        toast.success(isSpanish ? 'Proveedor actualizado' : 'Supplier updated');
      } else {
        await createSupplier(supplierForm);
        toast.success(isSpanish ? 'Proveedor creado' : 'Supplier created');
      }
      setIsSupplierDialogOpen(false);
      setEditingSupplier(null);
      setSupplierForm({ name: '', contact_name: '', email: '', phone: '', address: '', tax_id: '', category: '', payment_terms: 30, notes: '' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    try {
      const response = await createPurchaseOrder(poForm);
      if (poInvoiceFile) {
        const formData = new FormData();
        formData.append('file', poInvoiceFile);
        await uploadPurchaseOrderInvoice(response.data._id, formData);
      }
      toast.success(poInvoiceFile
        ? (isSpanish ? 'Orden creada con factura adjunta' : 'Purchase order created with invoice attached')
        : (isSpanish ? 'Orden creada sin factura validada' : 'Purchase order created without a validated invoice')
      );
      setIsPODialogOpen(false);
      setPOForm({ supplier_id: '', description: '', subtotal: 0, tax_amount: 0, total: 0, expected_date: '', notes: '' });
      setPOInvoiceFile(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleInvoiceFileChange = (file) => {
    if (!file) {
      setPOInvoiceFile(null);
      return;
    }
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      toast.error(isSpanish ? 'La factura debe ser PDF, PNG, JPEG o WebP' : 'Invoice must be PDF, PNG, JPEG, or WebP');
      setPOInvoiceFile(null);
      return;
    }
    if (file.size > maxSize) {
      toast.error(isSpanish ? 'La factura no puede superar 5 MB' : 'Invoice cannot exceed 5 MB');
      setPOInvoiceFile(null);
      return;
    }
    setPOInvoiceFile(file);
  };

  const clearInvoiceFile = () => {
    setPOInvoiceFile(null);
    if (invoiceInputRef.current) {
      invoiceInputRef.current.value = '';
    }
    if (invoiceUploadInputRef.current) {
      invoiceUploadInputRef.current.value = '';
    }
  };

  const formatFileSize = (size) => {
    if (!size) return '0 KB';
    if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleEditSupplier = (sup) => {
    setEditingSupplier(sup);
    setSupplierForm({ name: sup.name || '', contact_name: sup.contact_name || '', email: sup.email || '', phone: sup.phone || '', address: sup.address || '', tax_id: sup.tax_id || '', category: sup.category || '', payment_terms: sup.payment_terms || 30, notes: sup.notes || '' });
    setIsSupplierDialogOpen(true);
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm(isSpanish ? 'Eliminar proveedor?' : 'Delete supplier?')) return;
    try {
      await deleteSupplier(id);
      toast.success(isSpanish ? 'Proveedor eliminado' : 'Supplier deleted');
      loadData();
    } catch (error) { toast.error('Error'); }
  };

  const openCancelPODialog = (po) => {
    setSelectedPO(po);
    setBankTransferNumber(po.bank_transfer_number || '');
    setIsCancelPODialogOpen(true);
  };

  const openInvoiceDialog = (po) => {
    setSelectedPO(po);
    clearInvoiceFile();
    setIsInvoiceDialogOpen(true);
  };

  const handleCancelPO = async (e) => {
    e.preventDefault();
    if (!bankTransferNumber.trim()) {
      toast.error(isSpanish ? 'Introduce la referencia de pago' : 'Enter the payment reference');
      return;
    }
    try {
      await updatePurchaseOrder(selectedPO._id, {
        status: 'cancelada',
        bank_transfer_number: bankTransferNumber.trim(),
      });
      toast.success(isSpanish ? 'Orden cancelada y factura validada' : 'Purchase order cancelled and invoice validated');
      setIsCancelPODialogOpen(false);
      setSelectedPO(null);
      setBankTransferNumber('');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleUploadInvoice = async (e) => {
    e.preventDefault();
    if (!selectedPO) return;
    if (!poInvoiceFile) {
      toast.error(isSpanish ? 'Selecciona una factura' : 'Select an invoice');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', poInvoiceFile);
      await uploadPurchaseOrderInvoice(selectedPO._id, formData);
      toast.success(isSpanish ? 'Factura adjuntada' : 'Invoice attached');
      setIsInvoiceDialogOpen(false);
      setSelectedPO(null);
      clearInvoiceFile();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeletePO = async (po) => {
    const confirmed = window.confirm(
      isSpanish
        ? `Eliminar la orden ${po.po_number}?`
        : `Delete purchase order ${po.po_number}?`
    );
    if (!confirmed) return;
    try {
      await deletePurchaseOrder(po._id);
      toast.success(isSpanish ? 'Orden eliminada' : 'Purchase order deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDownloadInvoice = async (po) => {
    try {
      const response = await downloadPurchaseOrderInvoice(po._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = po.invoice_attachment?.filename || `${po.po_number}-factura`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(isSpanish ? 'No se pudo descargar la factura' : 'Could not download invoice');
    }
  };

  const poStatusLabel = (status) => {
    if (status === 'cancelada') return isSpanish ? 'Cancelada' : 'Cancelled';
    return isSpanish ? 'Pendiente' : 'Pending';
  };

  const invoiceStatusTone = (po) => {
    if (po.invoice_status === 'pending_validation' && po.invoice_attachment) {
      return 'warning';
    }
    if (po.invoice_validated) {
      return 'success';
    }
    return 'danger';
  };

  const invoiceStatusLabel = (po) => {
    if (po.invoice_status === 'pending_validation' && po.invoice_attachment) {
      return isSpanish ? 'Por validar' : 'Pending';
    }
    if (po.invoice_validated) {
      return isSpanish ? 'Validada' : 'Validated';
    }
    return isSpanish ? 'Invalidada' : 'Invalidated';
  };

  const purchaseOrderColumns = createPurchaseOrderColumns({
    isSpanish,
    poStatusLabel,
    invoiceStatusLabel,
    invoiceStatusTone,
    onDownloadInvoice: handleDownloadInvoice,
    onOpenInvoiceDialog: openInvoiceDialog,
    onOpenCancelDialog: openCancelPODialog,
    onDeletePurchaseOrder: handleDeletePO,
  });

  const renderPurchaseOrderActions = (po) => (
    <>
      {po.status !== 'cancelada' && (
        <ActionButton
          icon={Paperclip}
          label={po.invoice_attachment ? (isSpanish ? 'Reemplazar' : 'Replace') : (isSpanish ? 'Factura' : 'Invoice')}
          tone="default"
          onClick={() => openInvoiceDialog(po)}
        />
      )}
      {po.status !== 'cancelada' && (
        <ActionButton
          icon={CheckCircle}
          label={isSpanish ? 'Pagar' : 'Pay'}
          tone="approve"
          onClick={() => openCancelPODialog(po)}
        />
      )}
      <ActionButton
        icon={Trash}
        tone="delete"
        iconOnly
        onClick={() => handleDeletePO(po)}
      />
    </>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="suppliers-page">
        <PageHeader
          icon={Truck}
          title={isSpanish ? 'Proveedores y Compras' : 'Suppliers & Purchases'}
          actions={
            <ActionGroup>
              <ActionButton icon={ArrowClockwise} tone="default" iconOnly onClick={loadData} />
              <ActionButton
                icon={Plus}
                label={isSpanish ? 'Nuevo Proveedor' : 'New Supplier'}
                mobileLabel={isSpanish ? 'Proveedor' : 'Supplier'}
                tone="primary"
                onClick={() => {
                  setEditingSupplier(null);
                  setSupplierForm({ name: '', contact_name: '', email: '', phone: '', address: '', tax_id: '', category: '', payment_terms: 30, notes: '' });
                  setIsSupplierDialogOpen(true);
                }}
              />
              <ActionButton
                icon={ShoppingCart}
                label={isSpanish ? 'Nueva OC' : 'New PO'}
                mobileLabel={isSpanish ? 'OC' : 'PO'}
                tone="info"
                onClick={() => setIsPODialogOpen(true)}
              />
            </ActionGroup>
          }
        />

        {accountsPayable && (
          <StatsGrid className="md:grid-cols-3 xl:grid-cols-3">
            <MetricCard label={isSpanish ? 'Total por Pagar' : 'Total Payable'} value={`$${accountsPayable.total_payable?.toLocaleString()}`} tone="danger" />
            <MetricCard label={isSpanish ? 'Total Pagado' : 'Total Paid'} value={`$${accountsPayable.total_paid?.toLocaleString()}`} tone="success" />
            <MetricCard label={isSpanish ? 'Balance Pendiente' : 'Pending Balance'} value={`$${accountsPayable.balance?.toLocaleString()}`} tone="default" />
          </StatsGrid>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1E2636]">
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              {isSpanish ? 'Proveedores' : 'Suppliers'} ({suppliers.length})
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              {isSpanish ? 'Ordenes de Compra' : 'Purchase Orders'} ({purchaseOrders.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="suppliers" className="mt-4">
            {suppliers.length === 0 ? (
              <EmptyState icon={Truck} title={isSpanish ? 'Sin proveedores' : 'No suppliers'} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {suppliers.map(sup => (
                  <div key={sup._id} className="valhalla-card p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-[#F3F4F6] font-semibold">{sup.name}</p>
                        <p className="text-sm text-[#9CA3AF]">{sup.contact_name}</p>
                      </div>
                      <StatusBadge value={sup.status} />
                    </div>
                    {sup.email && <p className="text-sm text-[#9CA3AF]">{sup.email}</p>}
                    {sup.phone && <p className="text-sm text-[#9CA3AF]">{sup.phone}</p>}
                    {sup.category && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mt-2">{sup.category}</Badge>}
                    <ActionGroup className="mt-4 border-t border-[#242E42] pt-3" align="start">
                      <ActionButton icon={PencilSimple} tone="edit" iconOnly onClick={() => handleEditSupplier(sup)} />
                      <ActionButton icon={Trash} tone="delete" iconOnly onClick={() => handleDeleteSupplier(sup._id)} />
                    </ActionGroup>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            <DataTable
              columns={purchaseOrderColumns}
              data={purchaseOrders}
              getRowKey={(po) => po._id}
              loading={loading}
              loadingLabel={isSpanish ? 'Cargando...' : 'Loading...'}
              emptyIcon={ShoppingCart}
              emptyTitle={isSpanish ? 'Sin ordenes de compra' : 'No purchase orders'}
              mobileTitle={(po) => po.po_number}
              mobileSubtitle={(po) => po.supplier_name}
              mobileBadge={(po) => (
                <StatusBadge
                  value={po.status}
                  label={poStatusLabel(po.status)}
                  tone={po.status === 'cancelada' ? 'success' : 'warning'}
                  statusContext="purchase_order"
                />
              )}
              mobileActions={renderPurchaseOrderActions}
            />
          </TabsContent>
        </Tabs>

        <SupplierDialog
          open={isSupplierDialogOpen}
          onOpenChange={setIsSupplierDialogOpen}
          editingSupplier={editingSupplier}
          formData={supplierForm}
          setFormData={setSupplierForm}
          isSpanish={isSpanish}
          onSubmit={handleCreateSupplier}
          onCancel={() => setIsSupplierDialogOpen(false)}
        />

        <PurchaseOrderDialog
          open={isPODialogOpen}
          onOpenChange={setIsPODialogOpen}
          formData={poForm}
          setFormData={setPOForm}
          suppliers={suppliers}
          invoiceInputRef={invoiceInputRef}
          invoiceFile={poInvoiceFile}
          onInvoiceFileChange={handleInvoiceFileChange}
          onClearInvoiceFile={clearInvoiceFile}
          onSelectInvoiceFile={() => invoiceInputRef.current?.click()}
          formatFileSize={formatFileSize}
          isSpanish={isSpanish}
          onSubmit={handleCreatePO}
          onCancel={() => setIsPODialogOpen(false)}
        />

        <PurchaseOrderInvoiceDialog
          open={isInvoiceDialogOpen}
          onOpenChange={(open) => {
            setIsInvoiceDialogOpen(open);
            if (!open) {
              setSelectedPO(null);
              clearInvoiceFile();
            }
          }}
          selectedPO={selectedPO}
          invoiceUploadInputRef={invoiceUploadInputRef}
          invoiceFile={poInvoiceFile}
          onInvoiceFileChange={handleInvoiceFileChange}
          onClearInvoiceFile={clearInvoiceFile}
          onSelectInvoiceFile={() => invoiceUploadInputRef.current?.click()}
          formatFileSize={formatFileSize}
          isSpanish={isSpanish}
          onSubmit={handleUploadInvoice}
          onCancel={() => setIsInvoiceDialogOpen(false)}
        />

        <PurchaseOrderCancelDialog
          open={isCancelPODialogOpen}
          onOpenChange={setIsCancelPODialogOpen}
          selectedPO={selectedPO}
          bankTransferNumber={bankTransferNumber}
          setBankTransferNumber={setBankTransferNumber}
          isSpanish={isSpanish}
          onSubmit={handleCancelPO}
          onCancel={() => setIsCancelPODialogOpen(false)}
        />

      </div>
    </Layout>
  );
}
